<div class="p-6 bg-gray-50 min-h-screen">
  <!-- Toast Notification -->
  <div *ngIf="notification.show" class="fixed top-6 right-6 z-50 transition-all transform duration-300 animate-slide-in">
    <div [ngClass]="{
      'bg-emerald-600 text-white': notification.type === 'success',
      'bg-red-600 text-white': notification.type === 'error',
      'bg-blue-600 text-white': notification.type === 'info'
    }" class="px-5 py-3.5 rounded-xl shadow-xl flex items-center space-x-3 max-w-md">
      <i class="fas" [ngClass]="{
        'fa-check-circle': notification.type === 'success',
        'fa-exclamation-circle': notification.type === 'error',
        'fa-info-circle': notification.type === 'info'
      }"></i>
      <span class="text-sm font-medium flex-1">{{ notification.message }}</span>
      <button (click)="dismissNotification()" class="text-white hover:opacity-80 transition-opacity">
        <i class="fas fa-times text-xs"></i>
      </button>
    </div>
  </div>

  <!-- Page Header -->
  <div class="bg-gradient-to-r from-emerald-600 to-teal-800 text-white p-6 sm:p-8 rounded-2xl shadow-md mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
    <div>
      <div class="flex items-center space-x-3">
        <div class="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
          <i class="fas fa-calendar-day text-2xl text-emerald-200"></i>
        </div>
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold tracking-tight">Holiday Management</h1>
          <p class="text-emerald-100 text-sm sm:text-base mt-1">Configure company holidays, zone closures, and slot-level operational blocks</p>
        </div>
      </div>
    </div>
    <div class="flex items-center space-x-3" *appHasPermission="[P.HOLIDAY_CREATE, P.HOLIDAY_MANAGE, P.IS_CC_ADMIN]">
      <button (click)="openCreateModal()"
        class="inline-flex items-center space-x-2 bg-white text-emerald-700 hover:bg-emerald-50 px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow transition-all duration-200 cursor-pointer">
        <i class="fas fa-plus"></i>
        <span>Add Holiday</span>
      </button>
    </div>
  </div>

  <!-- Filters & Search Bar -->
  <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <!-- Search Input -->
      <div>
        <label class="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Search</label>
        <div class="relative">
          <i class="fas fa-search absolute left-3.5 top-3 text-gray-400 text-sm"></i>
          <input type="text" [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange()"
            placeholder="Search holiday name..."
            class="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" />
        </div>
      </div>

      <!-- Zone Filter -->
      <div>
        <label class="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Geo Zone</label>
        <select [(ngModel)]="selectedZoneFilter" (ngModelChange)="onFilterChange()"
          class="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white">
          <option value="">All Zones (Global &amp; Specific)</option>
          <option *ngFor="let zone of zones" [value]="zone.id">{{ zone.name }}</option>
        </select>
      </div>

      <!-- From Date -->
      <div>
        <label class="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">From Date</label>
        <input type="date" [(ngModel)]="fromDateFilter" (ngModelChange)="onFilterChange()"
          class="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" />
      </div>

      <!-- To Date -->
      <div>
        <label class="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">To Date</label>
        <input type="date" [(ngModel)]="toDateFilter" (ngModelChange)="onFilterChange()"
          class="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" />
      </div>
    </div>

    <!-- Quick Timeline Tabs & Refresh -->
    <div class="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100">
      <div class="flex items-center space-x-2">
        <span class="text-xs font-medium text-gray-500 mr-1">View:</span>
        <button (click)="setFilterType('all')"
          [ngClass]="filterType === 'all' ? 'bg-emerald-100 text-emerald-800 font-semibold' : 'text-gray-600 hover:bg-gray-100'"
          class="px-3 py-1 text-xs rounded-lg transition-colors cursor-pointer">
          All ({{ holidays.length }})
        </button>
        <button (click)="setFilterType('upcoming')"
          [ngClass]="filterType === 'upcoming' ? 'bg-emerald-100 text-emerald-800 font-semibold' : 'text-gray-600 hover:bg-gray-100'"
          class="px-3 py-1 text-xs rounded-lg transition-colors cursor-pointer">
          Upcoming
        </button>
        <button (click)="setFilterType('past')"
          [ngClass]="filterType === 'past' ? 'bg-emerald-100 text-emerald-800 font-semibold' : 'text-gray-600 hover:bg-gray-100'"
          class="px-3 py-1 text-xs rounded-lg transition-colors cursor-pointer">
          Past
        </button>
      </div>

      <button (click)="loadHolidays()" [disabled]="isLoading"
        class="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer">
        <i class="fas fa-sync-alt" [class.fa-spin]="isLoading"></i>
        <span>Refresh</span>
      </button>
    </div>
  </div>

  <!-- Holidays List / Table -->
  <div class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-20">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mb-3"></div>
      <p class="text-sm text-gray-500 font-medium">Loading holidays...</p>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isLoading && filteredHolidays.length === 0" class="text-center py-16 px-4">
      <div class="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <i class="fas fa-calendar-times text-emerald-500 text-2xl"></i>
      </div>
      <h3 class="text-base font-semibold text-gray-800 mb-1">No holidays found</h3>
      <p class="text-sm text-gray-500 max-w-md mx-auto mb-5">
        There are no holidays configured for the selected filters and date range.
      </p>
      <button (click)="openCreateModal()" *appHasPermission="[P.HOLIDAY_CREATE, P.HOLIDAY_MANAGE, P.IS_CC_ADMIN]"
        class="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer">
        <i class="fas fa-plus"></i>
        <span>Add Holiday</span>
      </button>
    </div>

    <!-- Data Table -->
    <div *ngIf="!isLoading && filteredHolidays.length > 0" class="overflow-x-auto">
      <table class="w-full text-left border-collapse">
        <thead>
          <tr class="bg-gray-50/80 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            <th class="py-3.5 px-6">Holiday Date</th>
            <th class="py-3.5 px-6">Holiday Name</th>
            <th class="py-3.5 px-6">Applicable Zone</th>
            <th class="py-3.5 px-6">Time Slot</th>
            <th class="py-3.5 px-6 text-right">Status</th>
            <th class="py-3.5 px-6 text-right" *appHasPermission="[P.HOLIDAY_MANAGE, P.IS_CC_ADMIN]">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 text-sm">
          <tr *ngFor="let holiday of filteredHolidays" class="hover:bg-gray-50/60 transition-colors">
            <!-- Date -->
            <td class="py-4 px-6 font-medium text-gray-900 whitespace-nowrap">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 rounded-xl flex flex-col items-center justify-center text-center shadow-xs"
                  [ngClass]="{
                    'bg-emerald-50 text-emerald-700 border border-emerald-200': isUpcoming(holiday.date),
                    'bg-amber-50 text-amber-700 border border-amber-200': isToday(holiday.date),
                    'bg-gray-100 text-gray-600 border border-gray-200': isPast(holiday.date)
                  }">
                  <span class="text-[10px] font-bold uppercase leading-none">{{ holiday.date | date:'MMM' }}</span>
                  <span class="text-sm font-extrabold leading-none mt-0.5">{{ holiday.date | date:'dd' }}</span>
                </div>
                <div>
                  <div class="font-semibold text-gray-800">{{ holiday.date | date:'fullDate' }}</div>
                  <div class="text-xs text-gray-400">{{ holiday.date | date:'EEEE' }}</div>
                </div>
              </div>
            </td>

            <!-- Holiday Name -->
            <td class="py-4 px-6">
              <div class="font-semibold text-gray-800">{{ holiday.name }}</div>
            </td>

            <!-- Scope / GeoZone -->
            <td class="py-4 px-6 whitespace-nowrap">
              <div class="flex items-center space-x-2">
                <i class="fas" [ngClass]="holiday.geoZoneId ? 'fa-map-pin text-emerald-600' : 'fa-globe text-blue-500'"></i>
                <span class="text-gray-700 font-medium">{{ getZoneName(holiday.geoZoneId) }}</span>
              </div>
            </td>

            <!-- Time Slot -->
            <td class="py-4 px-6 whitespace-nowrap">
              <div class="flex items-center space-x-2">
                <i class="far fa-clock text-gray-400"></i>
                <span class="text-gray-700">{{ getTimeSlotLabel(holiday.timeSlot) }}</span>
              </div>
            </td>

            <!-- Status Badge -->
            <td class="py-4 px-6 text-right whitespace-nowrap">
              <span *ngIf="isToday(holiday.date)" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 animate-pulse">
                Today
              </span>
              <span *ngIf="isUpcoming(holiday.date)" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                Upcoming
              </span>
              <span *ngIf="isPast(holiday.date)" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                Completed
              </span>
            </td>

            <!-- Actions -->
            <td class="py-4 px-6 text-right whitespace-nowrap" *appHasPermission="[P.HOLIDAY_MANAGE, P.IS_CC_ADMIN]">
              <button type="button" (click)="deleteHoliday(holiday)" [disabled]="deletingId === holiday.id"
                class="inline-flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 cursor-pointer"
                title="Delete holiday">
                <i class="fas" [ngClass]="deletingId === holiday.id ? 'fa-spinner fa-spin' : 'fa-trash-alt'"></i>
                <span>Delete</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Table Footer / Count -->
    <div class="py-3 px-6 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
      <span>Showing {{ filteredHolidays.length }} of {{ holidays.length }} total holidays</span>
      <span>Control Center Holiday Configuration</span>
    </div>
  </div>

  <!-- Slide-Over Sheet for Add Holiday -->
  <!-- Backdrop (z-40) -->
  <div *ngIf="showCreateModal"
    class="fixed inset-0 bg-gray-900/50 backdrop-blur-xs z-40 transition-opacity animate-fade-in"
    (click)="closeCreateModal()"></div>

  <!-- Slide-Over Drawer (z-50) -->
  <div *ngIf="showCreateModal"
    class="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out animate-slide-in-right border-l border-gray-100">
    
    <!-- Drawer Header -->
    <div class="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex items-center justify-between shadow-xs">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center backdrop-blur-xs">
          <i class="fas fa-calendar-plus text-lg text-white"></i>
        </div>
        <div>
          <h3 class="text-lg font-bold tracking-tight">Add New Holiday</h3>
          <p class="text-xs text-emerald-100">Create a company or zone-specific holiday entry</p>
        </div>
      </div>
      <button (click)="closeCreateModal()"
        class="text-white/80 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
        title="Close panel">
        <i class="fas fa-times text-base"></i>
      </button>
    </div>

    <!-- Drawer Body (Scrollable Form) -->
    <form (ngSubmit)="onSubmit()" class="flex-1 flex flex-col justify-between overflow-hidden">
      <div class="flex-1 overflow-y-auto p-6 space-y-5">
        <!-- Holiday Name -->
        <div>
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
            Holiday Name <span class="text-red-500">*</span>
          </label>
          <input type="text" [(ngModel)]="holidayForm.name" name="holidayName"
            placeholder="e.g. Diwali, Republic Day, Annual Day"
            [disabled]="isSubmitting"
            [class.border-red-400]="formErrors['name']"
            class="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" />
          <p *ngIf="formErrors['name']" class="text-xs text-red-500 mt-1 flex items-center">
            <i class="fas fa-exclamation-circle mr-1"></i> {{ formErrors['name'] }}
          </p>
        </div>

        <!-- Date Picker -->
        <div>
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
            Holiday Date <span class="text-red-500">*</span>
          </label>
          <input type="date" [(ngModel)]="holidayForm.date" name="holidayDate"
            [disabled]="isSubmitting"
            [class.border-red-400]="formErrors['date']"
            class="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" />
          <p *ngIf="formErrors['date']" class="text-xs text-red-500 mt-1 flex items-center">
            <i class="fas fa-exclamation-circle mr-1"></i> {{ formErrors['date'] }}
          </p>
        </div>

        <!-- Applicable GeoZone -->
        <div>
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
            Applicable Geo Zone
          </label>
          <select [(ngModel)]="holidayForm.geoZoneId" name="geoZoneId" [disabled]="isSubmitting"
            class="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white">
            <option value="">Global (All Zones)</option>
            <option *ngFor="let zone of zones" [value]="zone.id">{{ zone.name }}</option>
          </select>
          <p class="text-[11px] text-gray-400 mt-1">Select "Global" for company-wide holidays or choose a specific operating zone.</p>
        </div>

        <!-- Time Slot (Optional) -->
        <div>
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
            Time Slot
          </label>
          <select [(ngModel)]="holidayForm.timeSlot" name="timeSlot" [disabled]="isSubmitting"
            class="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white">
            <option value="">Full Day (All Slots)</option>
            <option *ngFor="let slot of timeSlotOptions" [value]="slot">{{ slot }}</option>
          </select>
          <p class="text-[11px] text-gray-400 mt-1">Choose "Full Day" to block all slots on this date, or select a specific slot.</p>
        </div>

        <app-booking-conflict-panel *ngIf="createConflict" [conflict]="createConflict"
          (dismiss)="createConflict = null"></app-booking-conflict-panel>

        <!-- Info Box -->
        <div class="bg-emerald-50/70 border border-emerald-100 rounded-xl p-3.5 text-xs text-emerald-800 flex items-start space-x-2.5">
          <i class="fas fa-info-circle text-emerald-600 mt-0.5"></i>
          <div>
            Creating a holiday automatically prevents customers from booking appointments on the selected date/slot and registers agent attendance as <strong>Holiday</strong>.
          </div>
        </div>
      </div>

      <!-- Sticky Drawer Footer -->
      <div class="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
        <button type="button" (click)="closeCreateModal()" [disabled]="isSubmitting"
          class="px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200/70 rounded-xl transition-colors cursor-pointer">
          Cancel
        </button>
        <button type="submit" [disabled]="isSubmitting"
          class="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm hover:shadow transition-all duration-200 cursor-pointer disabled:opacity-50">
          <i *ngIf="isSubmitting" class="fas fa-spinner fa-spin"></i>
          <i *ngIf="!isSubmitting" class="fas fa-check"></i>
          <span>{{ isSubmitting ? 'Creating...' : 'Create Holiday' }}</span>
        </button>
      </div>
    </form>
  </div>
</div>
